import { nextTick, onBeforeUnmount, onMounted } from 'vue';
import { gsap, prefersReducedMotion } from '@/features/drivers-workspace/utils/gsapOrg';

const NAV_LINK_SELECTOR = '.sidebar-nav-link';

/**
 * GSAP polish for sidebar nav — icon pulse on navigate + entrance stagger.
 * Collapse/expand width is handled by useSidebarCollapseMotion.
 *
 * @param {import('vue').Ref<HTMLElement|null>} sidebarRef
 */
export function useSidebarNavMotion(sidebarRef) {
  let motionCtx = null;
  let pulseTween = null;
  let hasStaggered = false;

  const killPulse = () => {
    if (pulseTween) {
      pulseTween.kill();
      pulseTween = null;
    }
  };

  const findNavIcon = (path) => {
    const root = sidebarRef.value;
    if (!root || !path) return null;
    const link = root.querySelector(`[data-nav-path="${CSS.escape(path)}"]`);
    return link?.querySelector?.('.sidebar-nav-link__icon') ?? null;
  };

  const playNavIconPulse = (path) => {
    if (!path || prefersReducedMotion()) return;

    const icon = findNavIcon(path);
    if (!icon) return;

    killPulse();
    gsap.set(icon, { transformOrigin: '50% 50%', scale: 1 });

    pulseTween = gsap.timeline({
      onComplete: () => {
        pulseTween = null;
      },
    })
      .to(icon, { scale: 1.12, duration: 0.2, ease: 'sine.out' })
      .to(icon, { scale: 0.97, duration: 0.12, ease: 'sine.inOut' })
      .to(icon, { scale: 1, duration: 0.28, ease: 'sine.out', clearProps: 'transform' });
  };

  const playNavStagger = async () => {
    if (hasStaggered || prefersReducedMotion()) return;

    const root = sidebarRef.value;
    if (!root) return;

    await nextTick();

    if (root.classList.contains('sidebar-root--rail-layout')) {
      hasStaggered = true;
      return;
    }

    const links = root.querySelectorAll(NAV_LINK_SELECTOR);
    if (!links.length) return;

    // fromTo + onInterrupt: interrupted gsap.from left links at opacity 0 permanently.
    motionCtx = gsap.context(() => {
      gsap.fromTo(
        links,
        { opacity: 0, x: -5 },
        {
          opacity: 1,
          x: 0,
          duration: 0.55,
          stagger: {
            amount: 0.32,
            ease: 'sine.inOut',
          },
          ease: 'sine.out',
          clearProps: 'opacity,transform',
          onComplete: () => {
            hasStaggered = true;
          },
          onInterrupt: () => {
            gsap.set(links, { clearProps: 'opacity,transform' });
            hasStaggered = true;
          },
        },
      );
    }, root);
  };

  onMounted(() => {
    playNavStagger();
  });

  onBeforeUnmount(() => {
    killPulse();
    motionCtx?.revert();
    motionCtx = null;
  });

  return {
    playNavIconPulse,
    playNavStagger,
  };
}
